/**
 * React Query Provider
 *
 * Configures and provides React Query client for data fetching and state management.
 * Includes development tools and sensible defaults for caching, retries, and refetching.
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';

/**
 * Default configuration for React Query
 */
const queryConfig = {
  defaultOptions: {
    queries: {
      // Retry failed requests once
      retry: 1,

      // Don't refetch when window regains focus to avoid unnecessary network calls
      refetchOnWindowFocus: false,

      // Consider data stale after 5 minutes
      staleTime: 5 * 60 * 1000, // 5 minutes

      // Keep unused data in cache for 10 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime in v4)

      // Refetch in the background when data becomes stale
      refetchOnMount: true,

      // Refetch when network reconnects
      refetchOnReconnect: true,
    },
    mutations: {
      // Don't retry mutations by default to avoid duplicate actions
      retry: 0,

      // Network error handling
      networkMode: 'online' as const,
    },
  },
};

// Create a singleton QueryClient instance
const queryClient = new QueryClient(queryConfig);

/**
 * Props for QueryProvider component
 */
interface QueryProviderProps {
  children: React.ReactNode;
  client?: QueryClient;
}

/**
 * QueryProvider Component
 *
 * Wraps the application with React Query context and dev tools.
 * Accepts an optional custom client for testing purposes.
 *
 * @param children - Child components to be wrapped
 * @param client - Optional custom QueryClient for testing
 */
export function QueryProvider({ children, client = queryClient }: QueryProviderProps) {
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}

/**
 * Export the default query client for use in tests or imperative queries
 */
export { queryClient };

/**
 * Export query configuration for reference
 */
export { queryConfig };
